import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Quote } from '../types/quotes';

// Helper functions for PDF generation
const addHeader = (doc: jsPDF): void => {
  doc.setFontSize(24);
  doc.setTextColor(37, 99, 235); // Blue-600
  doc.text('Chimperator Live', 20, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Webentwicklung & Software', 20, 26);
};

const addClientInfo = (doc: jsPDF, quote: Quote, yStart: number, label: string): number => {
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(label, 20, yStart);
  
  doc.setFontSize(10);
  let currentY = yStart + 7;
  
  // Use company name if available, otherwise use client name
  const displayName = quote.companyName || quote.clientName;
  
  if (label === 'Kunde') {
    // Angebot: Kundenname, E-Mail, Firmenname, Projektname, Anschrift
    doc.setFont('helvetica', 'bold');
    doc.text(`Kundenname:`, 20, currentY);
    doc.setFont('helvetica', 'normal');
    currentY += 6;
    doc.text(quote.clientName, 20, currentY);
    currentY += 6;
    
    if (quote.clientEmail) {
      doc.setFont('helvetica', 'bold');
      doc.text(`E-Mail:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.clientEmail, 20, currentY);
      currentY += 6;
    }
    
    if (quote.companyName) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Firmenname:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.companyName, 20, currentY);
      currentY += 6;
    }
    
    if (quote.projectName) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Projektname:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.projectName, 20, currentY);
      currentY += 6;
    }
    
    // Anschrift (wenn vorhanden)
    if (quote.street || quote.postalCode || quote.city) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Rechnungsanschrift:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      if (quote.street) {
        doc.text(quote.street, 20, currentY);
        currentY += 6;
      }
      if (quote.postalCode || quote.city) {
        const addressLine = [quote.postalCode, quote.city].filter(Boolean).join(' ');
        doc.text(addressLine, 20, currentY);
        currentY += 6;
      }
    }
  } else {
    // Rechnung: Name, E-Mail, Firmenname, Projektname, Rechnungsanschrift
    doc.setFont('helvetica', 'bold');
    doc.text(`Name:`, 20, currentY);
    doc.setFont('helvetica', 'normal');
    currentY += 6;
    doc.text(displayName, 20, currentY);
    currentY += 6;
    
    if (quote.clientEmail) {
      doc.setFont('helvetica', 'bold');
      doc.text(`E-Mail:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.clientEmail, 20, currentY);
      currentY += 6;
    }
    
    if (quote.companyName) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Firmenname:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.companyName, 20, currentY);
      currentY += 6;
    }
    
    if (quote.projectName) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Projektname:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      doc.text(quote.projectName, 20, currentY);
      currentY += 6;
    }
    
    // Rechnungsanschrift (wenn vorhanden)
    if (quote.street || quote.postalCode || quote.city) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Rechnungsanschrift:`, 20, currentY);
      doc.setFont('helvetica', 'normal');
      currentY += 6;
      if (quote.street) {
        doc.text(quote.street, 20, currentY);
        currentY += 6;
      }
      if (quote.postalCode || quote.city) {
        const addressLine = [quote.postalCode, quote.city].filter(Boolean).join(' ');
        doc.text(addressLine, 20, currentY);
        currentY += 6;
      }
    }
  }
  
  return currentY;
};

const addModulesTable = (doc: jsPDF, quote: Quote, yStart: number, moduleLabel: string): number => {
  doc.setFontSize(12);
  doc.text(moduleLabel, 20, yStart);
  
  // Get module hours from configuration
  const configModules = (quote.configuration?.selectedModules as Array<{ name: string; hours: number }>) || [];
  const moduleMap = new Map(configModules.map(m => [m.name, m.hours]));
  
  // Build rows with module name and hours
  const moduleRows = quote.selectedModules.map(moduleName => {
    const moduleHours = moduleMap.get(moduleName);
    return [
      moduleName,
      moduleHours !== undefined ? `${moduleHours}h` : '-'
    ];
  });
  
  autoTable(doc, {
    startY: yStart + 5,
    head: [[moduleLabel === 'Leistungen' ? 'Leistung' : 'Modul', 'Stunden']],
    body: moduleRows,
    theme: 'striped',
    headStyles: { 
      fillColor: [37, 99, 235] as [number, number, number]
    },
    columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { halign: 'right', cellWidth: 40 }
    },
    didParseCell: (data) => {
      // Make header "Stunden" right-aligned
      if (data.section === 'head' && data.column.index === 1) {
        data.cell.styles.halign = 'right';
      }
    },
    margin: { left: 20, right: 20 }
  });
  
  return (doc as any).lastAutoTable.finalY;
};

const addPricingTable = (doc: jsPDF, quote: Quote, yStart: number, totalLabel: string): number => {
  const priceRows: string[][] = [];
  
  // Show additional hours if they exist (instead of "Stunden (Module)")
  if (quote.additionalHours && quote.additionalHours > 0) {
    priceRows.push(['Zusätzliche Stunden', `${quote.additionalHours}h`]);
  }
  
  // Show total hours
  priceRows.push(['Gesamtstunden', `${quote.totalHours}h`]);
  
  if (quote.discountPercent && quote.discountPercent > 0) {
    const subtotal = quote.amountTotal / (1 - quote.discountPercent / 100);
    priceRows.push(['Zwischensumme', `€${subtotal.toFixed(2)}`]);
    priceRows.push([
      `Rabatt (${quote.discountPercent}%)`,
      `-€${quote.discountAmount?.toFixed(2) || '0.00'}`
    ]);
  }
  
  priceRows.push([`**${totalLabel}**`, `**€${quote.amountTotal.toFixed(2)}**`]);
  
  autoTable(doc, {
    startY: yStart,
    body: priceRows,
    theme: 'plain',
    styles: { fontSize: 11 },
    columnStyles: {
      0: { fontStyle: 'normal' },
      1: { halign: 'right', fontStyle: 'bold' }
    },
    margin: { left: 20, right: 20 },
    didParseCell: (data) => {
      // Make last row (total) bold
      if (data.row.index === priceRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 12;
      }
    }
  });
  
  return (doc as any).lastAutoTable.finalY;
};

const addNotes = (doc: jsPDF, quote: Quote, yStart: number, notesLabel: string): number => {
  if (!quote.notes) {
    return yStart;
  }
  
  doc.setFontSize(12);
  doc.text(notesLabel, 20, yStart);
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const splitNotes = doc.splitTextToSize(quote.notes, 170);
  doc.text(splitNotes, 20, yStart + 6);
  
  return yStart + 6 + (splitNotes.length * 5);
};

const addFooter = (doc: jsPDF, quote: Quote, showStatus = false): void => {
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Chimperator Live | emanuel@chimperator-live.de', 20, pageHeight - 10);
  doc.text(
    `Erstellt von: ${quote.createdByUserName}`,
    20,
    pageHeight - 6
  );
  
  if (showStatus) {
    const statusLabels = {
      draft: 'Entwurf',
      submitted: 'Eingereicht',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    doc.text(
      `Status: ${statusLabels[quote.status]}`,
      150,
      pageHeight - 6,
      { align: 'right' }
    );
  }
};

// Internal function to generate PDF (quote or invoice)
const generatePDF = (
  quote: Quote,
  config: {
    title: string;
    idLabel: string;
    dateLabel: string;
    clientLabel: string;
    clientY: number;
    modulesLabel: string;
    pricingLabel: string;
    notesLabel: string;
    showStatus: boolean;
  }
): Blob => {
  const doc = new jsPDF();
  
  // Header
  addHeader(doc);
  
  // Title
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text(config.title, 20, 45);
  
  // ID and Date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(config.idLabel, 20, 52);
  doc.text(config.dateLabel, 20, 58);
  
  // Client Information
  const clientY = addClientInfo(doc, quote, config.clientY, config.clientLabel);
  
  // Modules Table
  const modulesY = addModulesTable(doc, quote, clientY + 10, config.modulesLabel);
  
  // Pricing Table
  const pricingY = addPricingTable(doc, quote, modulesY + 10, config.pricingLabel);
  
  // Notes
  addNotes(doc, quote, pricingY + 10, config.notesLabel);
  
  // Footer
  addFooter(doc, quote, config.showStatus);
  
  return doc.output('blob');
};

export const pdfService = {
  /**
   * Generate PDF for a quote
   * @param quote Quote data
   * @returns PDF as Blob
   */
  generateQuotePDF(quote: Quote): Blob {
    return generatePDF(quote, {
      title: 'Angebot',
      idLabel: `Angebots-ID: ${quote.id.substring(0, 8)}...`,
      dateLabel: `Erstellt am: ${quote.createdAt.toLocaleDateString('de-DE')}`,
      clientLabel: 'Kunde',
      clientY: 70,
      modulesLabel: 'Gewählte Module',
      pricingLabel: 'Gesamtpreis',
      notesLabel: 'Notizen',
      showStatus: true
    });
  },

  /**
   * Download PDF
   * @param quote Quote data
   * @param filename Optional filename
   */
  downloadPDF(quote: Quote, filename?: string): void {
    const pdf = this.generateQuotePDF(quote);
    const url = URL.createObjectURL(pdf);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `angebot-${quote.id.substring(0, 8)}-${new Date().toISOString().split('T')[0]}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Generate PDF for an invoice (based on completed quote)
   * @param quote Completed quote data
   * @param invoiceNumber Invoice number (e.g., RE-2025-001)
   * @returns PDF as Blob
   */
  generateInvoicePDF(quote: Quote, invoiceNumber: string): Blob {
    return generatePDF(quote, {
      title: 'Rechnung',
      idLabel: `Rechnungsnummer: ${invoiceNumber}`,
      dateLabel: `Rechnungsdatum: ${new Date().toLocaleDateString('de-DE')}`,
      clientLabel: 'Rechnungsempfänger',
      clientY: 80,
      modulesLabel: 'Leistungen',
      pricingLabel: 'Rechnungsbetrag',
      notesLabel: 'Hinweise',
      showStatus: false
    });
  }
};
